import { apiGet, apiPost } from '@/features/shared/utils/dataFetching'
import {
  generateSubstitutionsForProductsPayload,
  generateSubstitutionsPayload,
  normalizePreferenceResponse,
} from '@/features/shop/services/Substitutions/utils'
import { type QueryFunctionContext } from '@tanstack/react-query'
import {
  type SubstitutionsQueryKey,
  type SubstitutionPreferencesQueryKey,
  type SubstitutionsForProductsQueryKey,
} from '@/features/shop/services/Substitutions/queries'
import { getSubstitutionPreferenceKey } from '@/features/shop/services/Substitutions/keys'
import {
  type RetrievedPreferenceAPI,
  type BackupPreferences,
  type FetchPreferencesResponse,
  type SubstitutionsForProductResponse,
  type SubstitutionsForProductsResponse,
  type RetrievedPreference,
} from '@/features/shop/services/Substitutions/types'

export async function fetchSubstitutionPreferences(
  context: QueryFunctionContext<SubstitutionPreferencesQueryKey>,
): Promise<BackupPreferences> {
  const { signal, queryKey } = context
  const [, { productIds, storeParams }] = queryKey
  const { zip, metro_id, store_id, store_location_id } = storeParams
  const response = await apiPost<FetchPreferencesResponse>({
    config: {
      url: 'out-of-stock/preferences/fetch',
      signal,
      data: {
        zip,
        metro_id,
        store_id,
        store_location_id,
        product_ids: productIds,
      },
    },
    context,
    fetcherName: 'fetchSubstitutionPreferences',
  })
  const prefs = response || {}
  return Object.keys(prefs).reduce((prefsById: BackupPreferences, productId: string) => {
    const preference = prefs[productId]?.[0] || {}
    const normalizedPreference = normalizePreferenceResponse(preference)
    prefsById[productId] = normalizedPreference
    // Add each preference to the PreferenceQuery cache
    context.client.setQueryData<RetrievedPreference>(
      getSubstitutionPreferenceKey({
        productId: Number(productId),
        storeParams,
      }),
      normalizedPreference,
    )
    return prefsById
  }, {})
}

export const fetchSubstitutionPreference = async (
  context: QueryFunctionContext<ReturnType<typeof getSubstitutionPreferenceKey>>,
) => {
  const [, { productId, storeParams }] = context.queryKey
  const { zip, metro_id, store_id, store_location_id } = storeParams

  const response = await apiGet<RetrievedPreferenceAPI[]>({
    config: {
      url: 'out-of-stock/preference',
      params: {
        zip,
        metro_id,
        store_id,
        store_location_id,
        product_id: productId,
      },
    },
    context,
    fetcherName: 'fetchSubstitutionPreference',
  })
  return normalizePreferenceResponse(response[0])
}

export const fetchSubstitutions = async (context: QueryFunctionContext<SubstitutionsQueryKey>) => {
  const [, { productId, storeParams }] = context.queryKey
  const { zip, metro_id, store_id, store_location_id } = storeParams
  const response = await apiGet<SubstitutionsForProductResponse>({
    config: {
      url: 'out-of-stock/substitutions',
      params: {
        zip,
        metro_id,
        store_id,
        store_location_id,
        product_id: productId,
      },
    },
    context,
    fetcherName: 'fetchSubstitutions',
  })
  return generateSubstitutionsPayload(productId, response)
}

export const fetchSubstitutionsForProducts = async (
  context: QueryFunctionContext<SubstitutionsForProductsQueryKey>,
) => {
  const [, { productIds, storeParams }] = context.queryKey
  const { zip, metro_id, store_id, store_location_id } = storeParams
  const response = await apiPost<SubstitutionsForProductsResponse>({
    config: {
      url: 'out-of-stock/substitutions',
      data: {
        zip,
        metro_id,
        store_id,
        store_location_id,
        product_ids: productIds,
      },
    },
    context,
    fetcherName: 'fetchSubstitutionsForProducts',
  })
  return generateSubstitutionsForProductsPayload(response)
}
